/**
 * embedCatalog.js
 * ───────────────
 * Shared catalog of every embeddable MediaOS component.
 *
 * Imported by:
 *   - embedGenerator.vue  (the standalone Embed Code Generator page)
 *   - embedBlock.vue      (the CMS block that embeds components inline)
 *   - propertySidebar.vue (the CMS sidebar that renders config forms)
 *
 * Each entry contains:
 *   - id              Unique component identifier
 *   - category        Filter category key
 *   - icon            Font Awesome class (e.g. 'fa-bullhorn')
 *   - color           Brand color hex for the icon badge
 *   - name            Human-readable display name
 *   - tagline         One-line description
 *   - tag             The HTML custom element tag name
 *   - description     Longer description shown in the generator
 *   - benefits        Array of bullet-point strings (optional)
 *   - configComponent Vue component name string for the config form (optional)
 *   - params          Empty array placeholder when no config form (optional)
 */

export var CATEGORIES = [
	{ key: 'all',         label: 'All',           icon: 'fa-th' },
	{ key: 'advertising', label: 'Advertising',   icon: 'fa-bullhorn' },
	{ key: 'forms',       label: 'Forms',          icon: 'fa-pencil-square-o' },
	{ key: 'membership',  label: 'Membership',     icon: 'fa-star' },
	{ key: 'content',     label: 'Content',        icon: 'fa-newspaper-o' },
	{ key: 'directory',   label: 'Directory',      icon: 'fa-building' },
	{ key: 'ecommerce',   label: 'E-Commerce',     icon: 'fa-shopping-cart' },
	{ key: 'events',      label: 'Events',         icon: 'fa-calendar' },
	{ key: 'voting',      label: 'Best of',        icon: 'fa-trophy' },
	{ key: 'donations',   label: 'Donations',      icon: 'fa-heart' },
	{ key: 'forum',       label: 'Forum',          icon: 'fa-comments' },
	{ key: 'user',        label: 'User / Auth',    icon: 'fa-user' }
];

/**
 * `cmsBlock` (optional): the native page-editor block that covers the same
 * feature. CMS pickers (Add Block menu, Embed Component panel) hide entries
 * that carry it so a feature appears once; the Embeds screen for outside
 * websites still lists every component.
 */
export function cmsVisibleComponents() {
	return COMPONENTS.filter(function (c) { return !c.cmsBlock; });
}

export var COMPONENTS = [
	// ── Advertising ──────────────────────────────────────────────────────────
	{
		id: 'mos-creative',
		// The page editor has a native block for this; the CMS pickers show that block instead.
		cmsBlock: 'AdSlotBlock',
		category: 'advertising',
		icon: 'fa-bullhorn',
		color: '#FB6A00',
		name: 'Ad Unit',
		tagline: 'Display a served ad creative on any page',
		tag: 'mos-creative',
		description: 'Renders a MediaOS-served ad creative. Shows fallback content (e.g., a Google Ad or default image) when no MediaOS ad is available.',
		benefits: [
			'Serve targeted display ads directly from MediaOS',
			'Zero-JavaScript setup — just drop the tag',
			'Supports Google Ad Manager passback automatically'
		],
		configComponent: 'embed-ad-unit'
	},
	{
		id: 'mediaos-sponsor-tiers',
		category: 'advertising',
		icon: 'fa-certificate',
		color: '#FB6A00',
		name: 'Sponsor Tiers',
		tagline: 'Sponsor logo wall organized by tier level',
		tag: 'mediaos-sponsor-tiers',
		description: 'Renders a sponsor logo grid organized by sponsorship tier level. Pulls sponsor data from a MediaOS product configuration.',
		benefits: [
			'Showcase all your sponsors automatically',
			'Tier-based ordering highlights top sponsors',
			'Always current — updates when sponsors change'
		],
		configComponent: 'embed-sponsor-tiers'
	},

	// ── Forms ─────────────────────────────────────────────────────────────────
	{
		id: 'mediaos-form',
		category: 'forms',
		icon: 'fa-pencil-square-o',
		color: '#0080FF',
		name: 'Custom Form',
		tagline: 'Embed any MediaOS form on your website',
		tag: 'mediaos-form',
		description: 'Renders a fully-featured MediaOS form — contact forms, lead capture, event registrations, and more. Responses are tracked inside MediaOS automatically.',
		benefits: [
			'All form submissions captured in MediaOS',
			'Supports conditional logic, file uploads, and payments',
			'Styled to match your brand automatically'
		],
		configComponent: 'embed-form'
	},

	// ── Membership ────────────────────────────────────────────────────────────
	{
		id: 'mediaos-landing-page',
		category: 'membership',
		icon: 'fa-file-text',
		color: '#7300F7',
		name: 'Landing Page Widget',
		tagline: 'Embed a membership landing / sign-up page',
		tag: 'mediaos-landing-page',
		description: 'Displays a full membership landing page inline on any page — complete with plan details, pricing, and sign-up flow.',
		benefits: [
			'No redirect needed — sign-up stays on your site',
			'Drives higher conversion by reducing friction'
		],
		configComponent: 'embed-landing-page'
	},
	{
		id: 'mediaos-page-views-left',
		category: 'membership',
		icon: 'fa-eye',
		color: '#7300F7',
		name: 'Page Views Left',
		tagline: 'Show a metered paywall counter to visitors',
		tag: 'mediaos-page-views-left',
		description: 'Displays how many free article views a visitor has remaining before hitting the paywall. Encourages subscriptions.',
		benefits: [
			'Creates urgency and drives membership sign-ups',
			'Works alongside the Page Lock component'
		],
		params: []
	},
	{
		id: 'mediaos-one-page-subscribe',
		category: 'membership',
		icon: 'fa-credit-card',
		color: '#7300F7',
		name: 'One-Page Subscribe',
		tagline: 'Streamlined single-page membership checkout',
		tag: 'mediaos-one-page-subscribe',
		description: 'A streamlined one-page subscription checkout flow. Displays available membership offers and checkout in a single view. Optionally include a custom form step.',
		benefits: [
			'Reduces checkout friction for new subscribers',
			'Fully integrated with MediaOS payments and CRM',
			'Optionally include a custom form step in the flow'
		],
		configComponent: 'embed-one-page-subscribe'
	},
	{
		id: 'mediaos-perks',
		category: 'membership',
		icon: 'fa-gift',
		color: '#7300F7',
		name: 'Perks',
		tagline: 'Member perks and discounts catalog',
		tag: 'mediaos-perks',
		description: 'Displays the member perks catalog — discounts and benefits provided by local businesses to your site\'s members.',
		benefits: [
			'Adds tangible value to membership',
			'Encourages sign-ups by showcasing real benefits',
			'Auto-updated from MediaOS perks configuration'
		],
		params: []
	},

	// ── Content ───────────────────────────────────────────────────────────────
	{
		id: 'mediaos-flip-book',
		category: 'content',
		icon: 'fa-book',
		color: '#009961',
		name: 'Flip Book',
		tagline: 'Interactive digital edition reader with page-flip navigation',
		tag: 'mediaos-flip-book',
		description: 'Embeds a full digital flip book reader for a specific issue or a browsable library of all published issues. Supports two-page spread, thumbnails, zoom, and fullscreen.',
		benefits: [
			'Bring your print edition online with an interactive reader',
			'Clickable ad hotspots drive advertiser value',
			'Browsable issue library when no specific issue is selected'
		],
		configComponent: 'embed-flip-book'
	},
	{
		id: 'mediaos-content-feed',
		// The page editor has a native block for this; the CMS pickers show that block instead.
		cmsBlock: 'PostListBlock',
		category: 'content',
		icon: 'fa-newspaper-o',
		color: '#009961',
		name: 'Content Feed',
		tagline: 'Display a live feed of your latest articles',
		tag: 'mediaos-content-feed',
		description: 'Renders a dynamic, filterable feed of content from your MediaOS site. Supports category filtering, pagination, and custom layouts.',
		benefits: [
			'Keep your website fresh with auto-updating content',
			'Filter by category, tag, or custom criteria',
			'Works on any page — sidebars, homepages, landing pages'
		],
		configComponent: 'embed-content-feed'
	},
	{
		id: 'mediaos-content-submission',
		category: 'content',
		icon: 'fa-pencil-square',
		color: '#009961',
		name: 'Content Submission',
		tagline: 'Let readers submit their own content',
		tag: 'mediaos-content-submission',
		description: 'Displays a submission form that lets readers submit articles, press releases, or community stories. All submissions go into MediaOS for review.',
		benefits: [
			'Grow your content library with community contributions',
			'Built-in moderation workflow in MediaOS'
		],
		params: []
	},
	{
		id: 'mediaos-newsletter-subscribe',
		// The page editor has a native block for this; the CMS pickers show that block instead.
		cmsBlock: 'NewsletterSignupBlock',
		category: 'content',
		icon: 'fa-envelope',
		color: '#009961',
		name: 'Newsletter Subscribe',
		tagline: 'Email newsletter sign-up form',
		tag: 'mediaos-newsletter-subscribe',
		description: 'A clean, branded email sign-up form. Subscribers are added to your MediaOS newsletter list immediately.',
		benefits: [
			'Grow your email list from any page',
			'Double-opt-in supported to keep your list clean',
			'Integrates with all MediaOS email campaign tools'
		],
		configComponent: 'embed-newsletter-subscribe'
	},
	{
		id: 'mediaos-newsletter-manage',
		category: 'content',
		icon: 'fa-sliders',
		color: '#009961',
		name: 'Manage Subscriptions',
		tagline: 'Let logged-in users manage newsletter preferences',
		tag: 'mediaos-newsletter-manage',
		description: 'Allows a logged-in user to view and manage their newsletter subscription preferences — subscribing or unsubscribing from individual newsletters.',
		benefits: [
			'Reduces unsubscribes by giving users granular control',
			'No custom development needed'
		],
		params: []
	},
	{
		id: 'mediaos-newsletter-archive',
		category: 'content',
		icon: 'fa-archive',
		color: '#009961',
		name: 'Newsletter Archive',
		tagline: 'Browsable archive of past newsletter editions',
		tag: 'mediaos-newsletter-archive',
		description: 'Displays a browsable archive of past newsletter editions, grouped by year. Supports viewing individual newsletter HTML directly in the component.',
		benefits: [
			'Lets prospects preview your newsletter before subscribing',
			'Drives trust and improves sign-up conversion'
		],
		configComponent: 'embed-newsletter-archive'
	},
	{
		id: 'mediaos-newsletter-article-form',
		category: 'content',
		icon: 'fa-file-text-o',
		color: '#009961',
		name: 'Newsletter Article Form',
		tagline: 'Article submission form for newsletter editions',
		tag: 'mediaos-newsletter-article-form',
		description: 'Displays a form for submitting articles to a specific newsletter edition.',
		benefits: [
			'Streamlined contributor workflow',
			'Submissions tracked in MediaOS'
		],
		configComponent: 'embed-newsletter-article-form'
	},
	{
		id: 'mediaos-news-bite-display',
		category: 'content',
		icon: 'fa-th-list',
		color: '#009961',
		name: 'News Bites Feed',
		tagline: 'Short-form news feed in a multi-column layout',
		tag: 'mediaos-news-bite-display',
		description: 'Displays a multi-column feed of short-form "news bite" posts. Supports category filtering, image display, and opening articles in a new tab.',
		benefits: [
			'Great for sidebars, homepage widgets, and footers',
			'High density — shows more stories in less space',
			'Can be filtered to one business or category'
		],
		configComponent: 'embed-news-bite-display'
	},
	{
		id: 'mediaos-bookmarks',
		category: 'content',
		icon: 'fa-heart',
		color: '#009961',
		name: 'Bookmark Button',
		tagline: 'Let logged-in users bookmark pages or profiles',
		tag: 'mediaos-bookmarks',
		description: 'A heart/bookmark icon that lets logged-in users save the current page or a specific content object.',
		benefits: [
			'Increases session depth and return visits',
			'Works on articles, business profiles, and more'
		],
		params: []
	},
	{
		id: 'mediaos-manage-bookmarks',
		category: 'content',
		icon: 'fa-bookmark',
		color: '#009961',
		name: 'Manage Bookmarks',
		tagline: 'Display a user\'s saved bookmarks list',
		tag: 'mediaos-manage-bookmarks',
		description: 'Displays all of the current user\'s saved bookmarks in a list view. Requires the user to be logged in.',
		benefits: [
			'Keeps members coming back to their saved content',
			'No configuration required'
		],
		params: []
	},
	{
		id: 'mediaos-classifieds',
		category: 'content',
		icon: 'fa-list-alt',
		color: '#009961',
		name: 'Classifieds',
		tagline: 'Classified ads listing with month browsing',
		tag: 'mediaos-classifieds',
		description: 'Displays a classified ads listing with month/year browsing.',
		benefits: [
			'Additional revenue stream from classified ad listings',
			'Month-by-month archive browsing built in',
			'Zero setup — reads from your MediaOS account'
		],
		params: []
	},
	{
		id: 'mediaos-poll',
		category: 'content',
		icon: 'fa-bar-chart',
		color: '#009961',
		name: 'Poll',
		tagline: 'Interactive poll widget',
		tag: 'mediaos-poll',
		description: 'Embeds a live poll on any page. Results update in real time and are tracked inside MediaOS.',
		benefits: [
			'Boost page engagement and time-on-site',
			'Results visible in MediaOS analytics'
		],
		configComponent: 'embed-poll'
	},
	{
		id: 'mediaos-kb-display',
		category: 'content',
		icon: 'fa-book',
		color: '#009961',
		name: 'Knowledge Base',
		tagline: 'Searchable knowledge base / help centre',
		tag: 'mediaos-kb-display',
		description: 'Displays a searchable knowledge base with categorized articles.',
		benefits: [
			'Self-serve support reduces tickets',
			'Searchable and organized by category'
		],
		configComponent: 'embed-kb-display'
	},

	// ── Directory ─────────────────────────────────────────────────────────────
	{
		id: 'mediaos-business-directory',
		category: 'directory',
		icon: 'fa-building',
		color: '#EEBC00',
		name: 'Business Directory',
		tagline: 'Display a searchable local business directory',
		tag: 'mediaos-business-directory',
		description: 'Renders the full, searchable MediaOS business directory with maps, categories, and profile links.',
		benefits: [
			'New revenue stream — charge for directory listings',
			'Boosts local SEO for your publication',
			'Fully searchable with category filtering'
		],
		configComponent: 'embed-business-directory'
	},
	{
		id: 'mediaos-business-profile',
		category: 'directory',
		icon: 'fa-building-o',
		color: '#EEBC00',
		name: 'Business Profile',
		tagline: 'Embed a single business profile card',
		tag: 'mediaos-business-profile',
		description: 'Shows a rich profile card for a single business — logo, description, contact info, social links, and more.',
		benefits: [
			'Perfect for sponsor spotlight pages',
			'Always up-to-date with MediaOS data'
		],
		configComponent: 'embed-business-profile'
	},
	{
		id: 'mediaos-business-directory-lists',
		category: 'directory',
		icon: 'fa-list',
		color: '#EEBC00',
		name: 'Directory List',
		tagline: 'Business directory filtered to a curated list',
		tag: 'mediaos-business-directory-lists',
		description: 'Displays the business directory filtered to a specific curated list of businesses, with optional map view.',
		benefits: [
			'Showcase hand-picked sponsors or partners',
			'Useful for "Best of" or category spotlight pages'
		],
		configComponent: 'embed-business-directory-lists'
	},
	{
		id: 'mediaos-business-lists',
		category: 'directory',
		icon: 'fa-th-large',
		color: '#EEBC00',
		name: 'Business Lists Home',
		tagline: 'Grid of all curated business lists',
		tag: 'mediaos-business-lists',
		description: 'Displays the home screen for all business lists — a grid of all configured curated business lists that users can browse.',
		benefits: [
			'One-click access to all curated directories',
			'Great for category hub pages'
		],
		params: []
	},
	{
		id: 'mediaos-business-profile-list',
		category: 'directory',
		icon: 'fa-list-ul',
		color: '#EEBC00',
		name: 'Business Profile List',
		tagline: 'Scrollable card list from a business profile list',
		tag: 'mediaos-business-profile-list',
		description: 'Displays businesses from a specific profile list as a scrollable card list with optional map integration.',
		benefits: [
			'Compact, browsable view of a curated set of businesses',
			'Ideal for partner pages and sponsor grids'
		],
		configComponent: 'embed-business-profile-list'
	},
	{
		id: 'mediaos-expo-directory',
		category: 'directory',
		icon: 'fa-map',
		color: '#EEBC00',
		name: 'Expo / Floor Plan',
		tagline: 'Interactive expo floor plan with exhibitor directory',
		tag: 'mediaos-expo-directory',
		description: 'Embeds an interactive expo floor plan with clickable booth locations and an optional directory listing of exhibitors.',
		benefits: [
			'Helps attendees navigate your event',
			'Boosts sponsor value with interactive booth visibility',
			'Embedded directory updates in real time'
		],
		configComponent: 'embed-expo-directory'
	},

	// ── E-Commerce ────────────────────────────────────────────────────────────
	{
		id: 'mediaos-store',
		category: 'ecommerce',
		icon: 'fa-shopping-cart',
		color: '#E31515',
		name: 'Product Store',
		tagline: 'Embed a full e-commerce product store',
		tag: 'mediaos-store',
		description: 'Renders a complete MediaOS product store with cart, checkout, and payment processing.',
		benefits: [
			'Sell memberships, merchandise, and digital products',
			'Payments handled by MediaOS with your Stripe account',
			'Orders and customers synced into MediaOS'
		],
		configComponent: 'embed-store'
	},
	{
		id: 'mediaos-checkout-add-button',
		category: 'ecommerce',
		icon: 'fa-plus-circle',
		color: '#E31515',
		name: 'Add to Cart Button',
		tagline: 'Add a single-product buy button anywhere',
		tag: 'mediaos-checkout-add-button',
		description: 'A standalone button that adds a specific MediaOS offer/product to the shopping cart.',
		benefits: [
			'Sell specific products inline in blog posts or landing pages',
			'Increases impulse-buy conversion'
		],
		configComponent: 'embed-checkout-add-button'
	},
	{
		id: 'mediaos-checkout',
		category: 'ecommerce',
		icon: 'fa-credit-card',
		color: '#E31515',
		name: 'Checkout',
		tagline: 'Full checkout and cart UI',
		tag: 'mediaos-checkout',
		description: 'Renders the full MediaOS checkout experience including cart review, address entry, payment, and order confirmation.',
		benefits: [
			'Complete checkout flow — no external platform needed',
			'Cart contents managed globally across your site'
		],
		params: []
	},
	{
		id: 'mediaos-cart-icon',
		category: 'ecommerce',
		icon: 'fa-shopping-bag',
		color: '#E31515',
		name: 'Cart Icon',
		tagline: 'Shopping cart icon with item count for your nav',
		tag: 'mediaos-cart-icon',
		description: 'A compact shopping cart icon with item count badge. Click to open a mini cart dropdown.',
		benefits: [
			'Persistent cart indicator across all pages',
			'Matches your nav bar height automatically'
		],
		params: []
	},

	// ── Events ────────────────────────────────────────────────────────────────
	{
		id: 'mediaos-event-calendar',
		category: 'events',
		icon: 'fa-calendar',
		color: '#0080FF',
		name: 'Event Calendar',
		tagline: 'Display a live event calendar on your site',
		tag: 'mediaos-event-calendar',
		description: 'Renders a filterable calendar of upcoming events managed in MediaOS.',
		benefits: [
			'Keep your community informed of upcoming events',
			'Integrates with MediaOS event registration and ticketing',
			'Mobile-friendly calendar view'
		],
		params: []
	},

	// ── Best of Voting ────────────────────────────────────────────────────────
	{
		id: 'mediaos-best-of-voting-page',
		category: 'voting',
		icon: 'fa-trophy',
		color: '#EEBC00',
		name: 'Best of Voting',
		tagline: 'Full Best of voting ballot for your readers',
		tag: 'mediaos-best-of-voting-page',
		description: 'Embeds the complete Best of voting experience — readers log in, browse categories, and cast votes.',
		benefits: [
			'#1 reader engagement driver for local media',
			'Integrated with MediaOS membership — drives sign-ups',
			'Auto-closes when voting period ends'
		],
		configComponent: 'embed-voting-page'
	},
	{
		id: 'mediaos-best-of-winners',
		category: 'voting',
		icon: 'fa-star',
		color: '#EEBC00',
		name: 'Best of Winners',
		tagline: 'Display published Best of winners',
		tag: 'mediaos-best-of-winners',
		description: 'Shows the published winners for a Best of voting campaign, grouped by category.',
		benefits: [
			'Showcase winners and drive advertiser value',
			'Always reflects the latest published results'
		],
		configComponent: 'embed-voting-winners'
	},
	{
		id: 'mediaos-best-of-quick-vote-generator',
		category: 'voting',
		icon: 'fa-qrcode',
		color: '#EEBC00',
		name: 'Quick Vote Generator',
		tagline: 'QR code quick-vote generator for events',
		tag: 'mediaos-best-of-quick-vote-generator',
		description: 'Displays a QR code generator tool for creating quick-vote links.',
		benefits: [
			'Drive on-site voting at events and tradeshows',
			'Print-ready QR codes generated instantly'
		],
		params: []
	},

	// ── Donations ─────────────────────────────────────────────────────────────
	{
		id: 'mediaos-support-us',
		category: 'donations',
		icon: 'fa-heart',
		color: '#E31515',
		name: 'Support Us',
		tagline: 'Donation and membership fundraising widget',
		tag: 'mediaos-support-us',
		description: 'A flexible fundraising widget supporting monthly, yearly, one-time, and custom donation amounts.',
		benefits: [
			'Accept donations directly on any page',
			'Monthly and one-time donation options',
			'Preset amounts reduce decision friction'
		],
		configComponent: 'embed-support-us'
	},
	{
		id: 'mediaos-support-goal',
		category: 'donations',
		icon: 'fa-line-chart',
		color: '#E31515',
		name: 'Donation Goal',
		tagline: 'Animated fundraising progress bar',
		tag: 'mediaos-support-goal',
		description: 'Displays an animated progress bar showing progress toward a fundraising goal.',
		benefits: [
			'Social proof — shows community support in real time',
			'Creates urgency as goal approaches'
		],
		configComponent: 'embed-support-goal'
	},
	{
		id: 'mediaos-donors',
		category: 'donations',
		icon: 'fa-users',
		color: '#E31515',
		name: 'Donors List',
		tagline: 'Donor recognition wall',
		tag: 'mediaos-donors',
		description: 'Displays a grid of donor names pulled from a MediaOS donation campaign.',
		benefits: [
			'Recognize and celebrate your donors publicly',
			'Motivates others to donate to be recognized'
		],
		configComponent: 'embed-donors'
	},

	// ── Forum / Community ─────────────────────────────────────────────────────
	{
		id: 'mediaos-comment-feed',
		category: 'forum',
		icon: 'fa-comments-o',
		color: '#0080FF',
		name: 'Comment Feed',
		tagline: 'Threaded comment section for articles or groups',
		tag: 'mediaos-comment-feed',
		description: 'Displays a threaded list of comments for a specific post or group.',
		benefits: [
			'Drive community engagement directly on your content',
			'Supports both article comments and group discussions'
		],
		params: []
	},
	{
		id: 'mediaos-comments',
		category: 'forum',
		icon: 'fa-comment-o',
		color: '#0080FF',
		name: 'Comments',
		tagline: 'Lightweight article comments section',
		tag: 'mediaos-comments',
		description: 'Lightweight comments component for embedding below articles.',
		benefits: [
			'Drop-in replacement for WordPress comments',
			'Auto-detects the current article — no config needed'
		],
		params: []
	},
	{
		id: 'mediaos-forum-home',
		category: 'forum',
		icon: 'fa-globe',
		color: '#0080FF',
		name: 'Forum Home',
		tagline: 'Complete community forum home page',
		tag: 'mediaos-forum-home',
		description: 'The complete community forum home — includes tabbed navigation for news, groups, members, and newsletter management.',
		benefits: [
			'All community features in a single embedded view',
			'Keeps members on your site instead of social media'
		],
		params: []
	},
	{
		id: 'mediaos-groups',
		category: 'forum',
		icon: 'fa-users',
		color: '#0080FF',
		name: 'Community Groups',
		tagline: 'Directory of community groups to browse and join',
		tag: 'mediaos-groups',
		description: 'Displays a directory of community groups that users can browse and join.',
		benefits: [
			'Build niche communities within your platform',
			'Drives member-to-member engagement'
		],
		params: []
	},

	// ── User / Auth ───────────────────────────────────────────────────────────
	{
		id: 'mediaos-user-menu',
		category: 'user',
		icon: 'fa-user',
		color: '#1E2031',
		name: 'User Menu',
		tagline: 'Login/logout nav item for your header',
		tag: 'mediaos-user-menu',
		description: 'Renders a login/logout button in your site navigation. When logged in, shows the user\'s avatar and a dropdown with account links.',
		benefits: [
			'Zero-effort authentication UI for your website',
			'Syncs with MediaOS membership status in real time'
		],
		configComponent: 'embed-user-menu'
	},
	{
		id: 'mediaos-login-form',
		category: 'user',
		icon: 'fa-sign-in',
		color: '#1E2031',
		name: 'Login Form',
		tagline: 'Standalone login form embed',
		tag: 'mediaos-login-form',
		description: 'Displays the MediaOS login form inline on any page.',
		benefits: [
			'Keep users on your site during login',
			'Supports social login and magic link options'
		],
		params: []
	},
	{
		id: 'mediaos-register-form',
		category: 'user',
		icon: 'fa-user-plus',
		color: '#1E2031',
		name: 'Registration Form',
		tagline: 'New member / account registration form',
		tag: 'mediaos-register-form',
		description: 'Renders the full MediaOS registration form for new members.',
		benefits: [
			'Grow your member database directly from your website',
			'All registrations land in MediaOS automatically'
		],
		params: []
	},
	{
		id: 'mediaos-sso',
		category: 'user',
		icon: 'fa-key',
		color: '#1E2031',
		name: 'Single Sign-On',
		tagline: 'SSO provider buttons (Google, Microsoft, LinkedIn)',
		tag: 'mediaos-sso',
		description: 'Renders configured SSO provider buttons based on your MediaOS account settings.',
		benefits: [
			'Reduces sign-in friction with one-click social login',
			'Auto-hides unconfigured providers — no extra setup'
		],
		params: []
	}
];

/**
 * Look up a catalog entry by its component ID.
 * @param {string} id
 * @returns {Object|null}
 */
export function findComponentById(id) {
	for (var i = 0; i < COMPONENTS.length; i++) {
		if (COMPONENTS[i].id === id) return COMPONENTS[i];
	}
	return null;
}
